import { useCallback, useEffect, useState } from "react";

import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { SearchSettingsFooter } from "@/Common/Components/Settings/SearchSettingsFooter";
import { useEffectAfterMount } from "@/Common/CustomHooks/useEffectAfterMount";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { EmployeeHelper } from "@/Common/Helpers/EmployeeHelper";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CommunicationsUIStateInbox, PortalCommunicationSentFilterSettingStatus } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import { CommunicationsHelper } from "@/Communications/Helpers/CommunicationsHelper";
import { PortalCommunicationFilterSettingsType, PortalCommunicationInboxFilterSetting, PortalCommunicationSentFilterSetting } from "@/Communications/Models/PortalCommunication/PortalCommunicationFilterSettings";

import styles from "./PortalCommunicationFilterSettingContainer.module.scss";

interface IPortalCommunicationInboxFilterSettingProps {
    initialFilterSetting: PortalCommunicationFilterSettingsType;
    inboxType: CommunicationsUIStateInbox;
    onColumnOrderingButtonClick: () => void;
    onSave: (setting: PortalCommunicationFilterSettingsType, isSaveSettingsChecked: boolean) => void;
    onClose: () => void;
}

function initializeFilterSetting(init: Partial<PortalCommunicationFilterSettingsType>, isTypeSent: boolean): PortalCommunicationFilterSettingsType {
    if (isTypeSent) {
        return new PortalCommunicationSentFilterSetting(init);
    }
    return new PortalCommunicationInboxFilterSetting(init);
}

export function PortalCommunicationFilterSettingContainer(props: Readonly<IPortalCommunicationInboxFilterSettingProps>): JSX.Element {
    const { getText } = useTranslation();

    const isTypeSent = CommunicationsHelper.isInboxTypeSent(props.inboxType);

    const isPatientInitiatedMessagingEnabled = cacheStore.ClinicSettings.EnablePatientInitiatedBidirectionalCommunication;

    const [filterSetting, setFilterSetting] = useState(props.initialFilterSetting);
    const [isSaveSettingsChecked, setIsSaveSettingsChecked] = useState(false);
    const [priorities, setPriorities] = useState(
        () => EnumHelper
            .getNamesAndValues(SharedItemPriority)
            .map(
                p => new CheckboxUIModel({
                    Id: String(p.value),
                    Name: getText(`Enums_SharedItemPriority_${p.name}`),
                    Checked: props.initialFilterSetting.priorities.includes(p.value)
                })
            )
    );
    const [categories, setCategories] = useState<CheckboxUIModel[]>([]);
    const [statuses, setStatuses] = useState(() => {
        const setting = props.initialFilterSetting;
        if (setting instanceof PortalCommunicationSentFilterSetting) {
            return EnumHelper
                .getNamesAndValues(PortalCommunicationSentFilterSettingStatus)
                .map(p => new CheckboxUIModel({
                    Id: String(p.value),
                    Name: getText(`${p.name}Label`),
                    Checked: setting.statuses.includes(p.value)
                }));
        }

        return [];
    });
    const [sharedWithCliniciansUIModels, setSharedWithCliniciansUIModels] = useState<EmployeeUIModel[]>(
        () => cacheStore
            .GetCliniciansAsEmployees(CheckboxTarget.Myle_Communications_Messages_Search_SharedWith, true)
            .filter(p => cacheStore.EmployeeSharingPortalCommunication.contains(p.Id))
            .map(p => {
                p.Checked = props.initialFilterSetting.sharedWithClinicianContactIds.contains(p.Model.Contact_id);
                return p;
            })
    );

    const getSharedCategoriesAsync = useCallback(async (): Promise<void> => {
        const setting = props.initialFilterSetting;
        if (setting instanceof PortalCommunicationSentFilterSetting) {
            setCategories([]);
            return;
        }

        const categories = ((await cacheStore.getSharedItemCategories())
            .map(p => new CheckboxUIModel({
                Id: p.Id,
                Name: cacheStore.GetLocalizedValueByLookupId(p.NameLookupId),
                Checked: setting.categoryIds.includes(p.Id)
            })));

        setCategories(categories);
    }, [props.initialFilterSetting]);

    useEffect(() => {
        getSharedCategoriesAsync();
    }, [getSharedCategoriesAsync]);

    useEffect(() => {
        const overlayKey = "PortalCommunicationFilterSettingOverlay";

        overlayStore.showOverlay(overlayKey, {
            transparent: true,
            onClicked: () => {
                props.onSave(filterSetting, isSaveSettingsChecked);
                props.onClose();
            }
        });

        return () => {
            overlayStore.hideOverlay(overlayKey);
        };
    }, [props, isSaveSettingsChecked, filterSetting]);

    useEffectAfterMount(() => {
        if (isTypeSent) {
            return;
        }

        setFilterSetting(p => initializeFilterSetting(
            {
                ...p,
                priorities: priorities
                    .filter(priority => priority.Checked)
                    .map(priority => Number(priority.Id))
            },
            isTypeSent
        ));
    }, [isTypeSent, priorities]);

    useEffectAfterMount(() => {
        if (isTypeSent || !isPatientInitiatedMessagingEnabled) {
            return;
        }

        setFilterSetting(p => initializeFilterSetting(
            {
                ...p,
                categoryIds: categories
                    .filter(category => category.Checked)
                    .map(category => category.Id)
            },
            isTypeSent
        ));
    }, [isTypeSent, categories]);

    useEffectAfterMount(() => {
        if (!isTypeSent) {
            return;
        }

        setFilterSetting(p => initializeFilterSetting(
            {
                ...p,
                statuses: statuses
                    .filter(status => status.Checked)
                    .map(status => Number(status.Id))
            },
            isTypeSent
        ));
    }, [isTypeSent, statuses]);

    useEffectAfterMount(() => {
        setFilterSetting(p => initializeFilterSetting(
            {
                ...p,
                sharedWithClinicianContactIds: sharedWithCliniciansUIModels
                    .filter(clinicianUIModel => clinicianUIModel.Checked)
                    .map(clinicianUIModel => clinicianUIModel.Model.Contact_id)
            },
            isTypeSent
        ));
    }, [isTypeSent, sharedWithCliniciansUIModels]);

    useEffectAfterMount(() => {
        setFilterSetting(p => initializeFilterSetting(
            {
                ...p,
                priorities: priorities
                    .filter(model => model.Checked)
                    .map(model => Number(model.Id))
            },
            isTypeSent
        ));
    }, [isTypeSent, priorities]);

    function onClinicianSearchTextChange(searchText: string): void {
        if (IsNullOrUndefined(searchText)) {
            return;
        }

        setFilterSetting(p => initializeFilterSetting({ ...p, sharedWithClinicianSearchText: searchText.trim() }, isTypeSent));
    }

    const isInInbox = !isTypeSent && filterSetting instanceof PortalCommunicationInboxFilterSetting;
    const isInSent = isTypeSent && filterSetting instanceof PortalCommunicationSentFilterSetting;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    {getText(isTypeSent ? "SentDateLabel" : "ReceivedDateLabel").toUpperCase()}
                </div>
                <CheckboxControl
                    label={getText("SharedWithLabel").toUpperCase()}
                    checked={filterSetting.isAllSharedWithChecked ?? false}
                    onChange={isChecked => {
                        setFilterSetting(p => initializeFilterSetting({ ...p, isAllSharedWithChecked: isChecked }, isTypeSent));
                        setSharedWithCliniciansUIModels(p => p.map(model => new EmployeeUIModel({ ...model, Checked: isChecked })));
                    }}
                    DivWrap
                    dataCy="portalCommunicationFilterSettingContainer-shared-with-checkbox"
                />
                <div className={styles.half}>
                    <CheckboxControl
                        label={getText("PriorityLabel").toUpperCase()}
                        checked={filterSetting.isAllPrioritiesChecked ?? false}
                        onChange={isChecked => {
                            setFilterSetting(p => initializeFilterSetting({ ...p, isAllPrioritiesChecked: isChecked }, isTypeSent));
                            setPriorities(p => p.map(priority => new CheckboxUIModel({ ...priority, Checked: isChecked })));
                        }}
                        dataCy="portalCommunicationFilterSettingContainer-priority-checkbox"
                    />
                </div>
                {(isInInbox && isPatientInitiatedMessagingEnabled) && (
                    <CheckboxControl
                        label={getText("CategoryDLabel").toUpperCase()}
                        checked={filterSetting.isAllCategoryChecked}
                        onChange={isChecked => {
                            setFilterSetting(p => initializeFilterSetting({ ...p, isAllCategoryChecked: isChecked, isClinicInitiatedChecked: isChecked }, isTypeSent));
                            setCategories(p => p.map(category => new CheckboxUIModel({ ...category, Checked: isChecked })));
                        }}
                        DivWrap
                        dataCy="portalCommunicationFilterSettingContainer-category-checkbox"
                    />
                )}
                {isInSent && (
                    <div className={styles.half}>
                        <CheckboxControl
                            label={getText("StatusLabel").toUpperCase()}
                            checked={filterSetting.isAllStatusChecked}
                            onChange={isChecked => {
                                setFilterSetting(p => initializeFilterSetting({ ...p, isAllStatusChecked: isChecked }, isTypeSent));
                                setStatuses(p => p.map(status => new CheckboxUIModel({ ...status, Checked: isChecked })));
                            }}
                            dataCy="portalCommunicationFilterSettingContainer-status-checkbox"
                        />
                    </div>
                )}
            </div>
            <div className={styles.main}>
                <MultiDateSelector
                    id="PortalCommunicationInboxFilterSettingDates"
                    from={filterSetting.dateFrom}
                    to={filterSetting.dateTo}
                    fromSetTodayLabel={getText("TodayLabel")}
                    toSetTodayLabel={getText("TodayLabel")}
                    fromChange={date => setFilterSetting(p => initializeFilterSetting({ ...p, dateFrom: date }, isTypeSent))}
                    toChange={date => setFilterSetting(p => initializeFilterSetting({ ...p, dateTo: date }, isTypeSent))}
                    dataCy="portalCommunicationFilterSettingContainer-dates"
                />
                <div className={styles.section}>
                    <TextboxControl
                        className="form-control"
                        value={filterSetting.sharedWithClinicianSearchText}
                        onChange={(newText: string) => onClinicianSearchTextChange(newText)}
                        placeholder={getText("SearchLabel").toUpperCase()}
                        dataCy="portalCommunicationFilterSettingContainer-clinician-search-text"
                    />
                    <div data-cy="portalCommunicationFilterSettingContainer-clinician-checked-list">
                        <CheckedListControl
                            DataSource={EmployeeHelper
                                .filterCliniciansBySearchText(sharedWithCliniciansUIModels, filterSetting.sharedWithClinicianSearchText)
                                .toImmutable()
                            }
                            idModifier="portalCommunicationClinicians"
                            onChange={(isChecked, id) =>
                                setSharedWithCliniciansUIModels(
                                    p =>
                                        p.map(
                                            clinicianUIModel => {
                                                if (clinicianUIModel.Id === id) {
                                                    return new EmployeeUIModel({ ...clinicianUIModel, Checked: isChecked });
                                                }

                                                return clinicianUIModel;
                                            }
                                        )
                                )
                            }
                            sort
                            limitTextWidth
                        />
                    </div>
                </div>
                <div className={`${styles.section} ${styles.half}`}>
                    <CheckedListControl
                        DataSource={Immutable.List(priorities)}
                        idModifier="PortalCommunicationFilterSettingPriority"
                        onChange={(isChecked, id) => {
                            setPriorities(p =>
                                p.map(priority => priority.Id === id ? new CheckboxUIModel({ ...priority, Checked: isChecked }) : priority)
                            );
                        }}
                        dataCy="portalCommunicationFilterSettingContainer-priority-checked-list"
                    />
                </div>
                {(isInInbox && isPatientInitiatedMessagingEnabled) && (
                    <div className={styles.section}>
                        <CheckboxControl
                            label={getText("SharedItem_SharedItemCategory_ClinicInitiated")}
                            checked={filterSetting.isClinicInitiatedChecked || filterSetting.isAllCategoryChecked}
                            onChange={isChecked => {
                                setFilterSetting(p => initializeFilterSetting({ ...p, isClinicInitiatedChecked: isChecked }, isTypeSent));
                            }}
                            DivWrap
                            dataCy="portalCommunicationFilterSettingContainer-clinic-initiated-checkbox"
                        />

                        <CheckedListControl
                            DataSource={Immutable.List(categories)}
                            idModifier="PortalCommunicationFilterSettingCategory"
                            onChange={(isChecked, id) => {
                                setCategories(p =>
                                    p.map(category => category.Id === id ? new CheckboxUIModel({ ...category, Checked: isChecked }) : category)
                                );
                            }}
                            dataCy="portalCommunicationFilterSettingContainer-category-checked-list"
                        />
                    </div>
                )}
                {isInSent && (
                    <div
                        className={`${styles.section} ${styles.half}`}
                        data-cy="portalCommunicationFilterSettingContainer-status-checked-list"
                    >
                        <CheckedListControl
                            DataSource={Immutable.List(statuses)}
                            idModifier="PortalCommunicationFilterSettingStatus"
                            onChange={(isChecked, id) => {
                                setStatuses(p =>
                                    p.map(status => status.Id === id ? new CheckboxUIModel({ ...status, Checked: isChecked }) : status)
                                );
                            }}
                        />
                    </div>
                )}
            </div>
            <div className={styles.footer} data-cy="portalCommunicationFilterSettingContainer-search-setting-footer">
                <SearchSettingsFooter
                    onClose={() => props.onClose()}
                    onOk={() => props.onSave(filterSetting, isSaveSettingsChecked)}
                    onColumnOrderingButtonClick={() => props.onColumnOrderingButtonClick()}
                    onResultsPerPageChange={pageSize => setFilterSetting(p => initializeFilterSetting({ ...p, queryPageSize: pageSize }, isTypeSent))}
                    selectedPageSizeOption={filterSetting.queryPageSize}
                    onSaveSettingsCheckboxChange={checked => setIsSaveSettingsChecked(checked)}
                    isSaveSettingsChecked={isSaveSettingsChecked}
                    pageSizeOptions={[30, 50, 100]}
                />
            </div>
        </div>
    );
}
